export type FaceId =
  | "play"
  | "augment"
  | "mobile"
  | "react-native"
  | "ai"
  | "trade"
  | "build"
  | "engine";

export interface Face {
  id: FaceId;
  label: string;
  shortLabel: string;
  route: `/${FaceId}`;
  themeVar: `--color-jg-${FaceId}`;
  themeColor: string;
  blurb: string;
}

export const FACES: readonly Face[] = [
  {
    id: "play",
    label: "Play",
    shortLabel: "Play",
    route: "/play",
    themeVar: "--color-jg-play",
    themeColor: "#ff5c87",
    blurb: "Games — including Cubek, a Rubik's cube on the Play Store.",
  },
  {
    id: "augment",
    label: "Augment",
    shortLabel: "AR/VR",
    route: "/augment",
    themeVar: "--color-jg-augment",
    themeColor: "#a280ff",
    blurb: "AR & VR — Vuforia, AR Foundation, immersive UX.",
  },
  {
    id: "mobile",
    label: "Mobile",
    shortLabel: "Native",
    route: "/mobile",
    themeVar: "--color-jg-mobile",
    themeColor: "#45d3a8",
    blurb: "Native Android — Kotlin, Jetpack Compose, long-running ships.",
  },
  {
    id: "react-native",
    label: "React Native",
    shortLabel: "RN",
    route: "/react-native",
    themeVar: "--color-jg-react-native",
    themeColor: "#61dafb",
    blurb: "Cross-platform with React Native — one codebase, two stores.",
  },
  {
    id: "ai",
    label: "AI",
    shortLabel: "AI",
    route: "/ai",
    themeVar: "--color-jg-ai",
    themeColor: "#c7ff5c",
    blurb: "LLM apps, agentic workflows, model integration end-to-end.",
  },
  {
    id: "trade",
    label: "Trade",
    shortLabel: "Trade",
    route: "/trade",
    themeVar: "--color-jg-trade",
    themeColor: "#ffb74d",
    blurb: "Fintech & automation — meet Cypher Bot.",
  },
  {
    id: "build",
    label: "Build",
    shortLabel: "Build",
    route: "/build",
    themeVar: "--color-jg-build",
    themeColor: "#5cc0ff",
    blurb: "Ten years of shipping. The full career timeline.",
  },
  {
    id: "engine",
    label: "Engines",
    shortLabel: "Engine",
    route: "/engine",
    themeVar: "--color-jg-engine",
    themeColor: "#ff4d4d",
    blurb: "Unity & Unreal — shipping games, sims, and immersive tools.",
  },
] as const;

export const FACE_BY_ID: Record<FaceId, Face> = Object.fromEntries(
  FACES.map((f) => [f.id, f]),
) as Record<FaceId, Face>;

/**
 * 2×2×2 Rubik's-cube cubie positions. Each cubie sits at a corner of a unit
 * cube, with the most-strategic skills placed on cubies that face the camera
 * at the default angle ([3.5, 2.5, 3.5]).
 */
export const CUBIE_OFFSET = 0.5;
export const CUBIE_SIZE = 0.96;

export const CUBIE_POSITIONS: Record<FaceId, [number, number, number]> = {
  ai:             [+CUBIE_OFFSET, +CUBIE_OFFSET, +CUBIE_OFFSET], // hero — closest to camera
  play:           [-CUBIE_OFFSET, +CUBIE_OFFSET, +CUBIE_OFFSET],
  trade:          [+CUBIE_OFFSET, -CUBIE_OFFSET, +CUBIE_OFFSET],
  "react-native": [-CUBIE_OFFSET, -CUBIE_OFFSET, +CUBIE_OFFSET],
  augment:        [+CUBIE_OFFSET, +CUBIE_OFFSET, -CUBIE_OFFSET],
  mobile:         [-CUBIE_OFFSET, +CUBIE_OFFSET, -CUBIE_OFFSET],
  build:          [+CUBIE_OFFSET, -CUBIE_OFFSET, -CUBIE_OFFSET],
  engine:         [-CUBIE_OFFSET, -CUBIE_OFFSET, -CUBIE_OFFSET],
};
